import { isNum } from '../utils/common';

export interface ParseResultNums {
	nums: [number, number, number, number, number, number, number];
	tzOffsetMins: number;
	tzKnown: boolean;
}

export interface ParseResultTimestamp {
	ms: number;
	tzOffsetMins: number;
	tzKnown: boolean;
}

// RFC 2822 regex: For details see https://tools.ietf.org/html/rfc2822#section-3.3

const knownZones: Record<string, number> = {
	UT: 0,
	GMT: 0,
	Z: 0,
	EDT: -4 * 60,
	EST: -5 * 60,
	CDT: -5 * 60,
	CST: -6 * 60,
	MDT: -6 * 60,
	MST: -7 * 60,
	PDT: -7 * 60,
	PST: -8 * 60,
	// Australian Eastern Daylight Time
	AEDT: 11 * 60,
	// Australian Eastern Standard Time
	AEST: 10 * 60,
};

const knownMonthsShort: Record<string, number> = {
	JAN: 0,
	FEB: 1,
	MAR: 2,
	APR: 3,
	MAY: 4,
	JUN: 5,
	JUL: 6,
	AUG: 7,
	SEP: 8,
	OCT: 9,
	NOV: 10,
	DEC: 11,
};

// 17:59:59
// 17:59:59.00
// 17:59
const rxsTime = '(\\d{1,2}):(\\d\\d)(?::(\\d\\d)(\\.\\d*)?)?';
const rxsMonth = `(${Object.keys(knownMonthsShort).join('|')})`;
const rxsZone1 = `(?:${Object.keys(knownZones).join('|')})`;
const rxsZone2 = `(?:[+-]\\d{1,4})`;
const rxsZone = `(${rxsZone1}|${rxsZone2})`;
const rxZone = new RegExp(`^(${rxsZone1}|${rxsZone2})$`, 'i');

export const rxsDateTimeNominal = `(\\d{1,2})\\s+${rxsMonth}\\s+(\\d{2,4})\\s+${rxsTime}\\s+${rxsZone}`;
const rxDateTime1 = new RegExp(rxsDateTimeNominal, 'i');

export const rxsDateTimeNoZone = `(\\d{1,2})\\s+${rxsMonth}\\s+(\\d{2,4})\\s+${rxsTime}`;
const rxDateTime1NoZone = new RegExp(rxsDateTimeNoZone, 'i');

export const rxsDateTimeSwapped = `${rxsMonth}\\s+(\\d{1,2}),?\\s+(\\d{2,4})\\s+${rxsTime}\\s+${rxsZone}`;
const rxDateTime2 = new RegExp(rxsDateTimeSwapped, 'i');

export const rxsDateTimeYearAtEnd = `${rxsMonth}\\s+(\\d{1,2})\\s+${rxsTime}\\s+${rxsZone}\\s+(\\d{2,4})`;
const rxDateTime3 = new RegExp(rxsDateTimeYearAtEnd, 'i');

function preprocess(s: string) {
	// Remove comments and folding whitespace and replace multiple-spaces with a single space
	const comments: string[] = [];
	const s2 = s
		.replace(/\([^)]*\)|[\n\t]/g, match => {
			if (match.startsWith('(') && match.endsWith(')')) {
				comments.push(match.slice(1, match.length - 1));
			}
			return ' ';
		})
		.replace(/(\s\s+)/g, ' ')
		.replace(/^\s\s*/, '')
		.replace(/\s\s*$/, '');

	return { str: s2, comments: comments };
}

function untruncateYear(yearStr: string) {
	const year = Number.parseInt(yearStr, 10);
	if (year <= 49) {
		return 2000 + year;
	} else if (year <= 999) {
		return 1900 + year;
	}
	return year;
}

function getTzOffsetInMinutes(tz: string) {
	let tzOffsetMins: number;
	let tzKnown: boolean;

	const tzOffsetNamed = knownZones[tz.toUpperCase()];
	if (isNum(tzOffsetNamed)) {
		tzOffsetMins = tzOffsetNamed;
		tzKnown = true;
	} else if (tz === '-0000') {
		tzOffsetMins = 0;
		tzKnown = false;
	} else {
		const pos = tz[0] === '-' ? -1 : 1;
		if (tz[0] === '-' || tz[0] === '+') {
			tz = tz.slice(1);
		}

		tz = (tz + '0000').slice(0, 4);
		const hr = Number.parseInt(tz.slice(0, 2), 10);
		const mn = Number.parseInt(tz.slice(2, 4), 10);
		tzOffsetMins = pos * (hr * 60 + mn);
		tzKnown = true;
	}

	return {
		tzOffsetMins,
		tzKnown,
	};
}

function parse2(
	day: string,
	month: string,
	year: string,
	hr: string,
	min: string,
	sec: string,
	ms: string,
	tz: string
): ParseResultNums {
	return {
		nums: [
			untruncateYear(year),
			knownMonthsShort[month.toUpperCase()],
			Number.parseInt(day, 10),
			Number.parseInt(hr, 10),
			Number.parseInt(min, 10),
			sec ? Number.parseInt(sec, 10) : 0,
			ms ? Math.round(Number.parseFloat('0' + ms) * 1000) : 0,
		],
		...getTzOffsetInMinutes(tz),
	};
}

// date and time from ref 2822 format
export function parse(_input: string): ParseResultNums | undefined {
	const { str: input, comments } = preprocess(_input);

	{
		const match = rxDateTime1.exec(input);
		if (match) {
			return parse2(
				match[1],
				match[2],
				match[3],
				match[4],
				match[5],
				match[6],
				match[7],
				match[8]
			);
		}
	}

	// Handle case where TimeZone is technically a comment... Tue, 23 Mar 2004 22:08:29 (GMT)
	if (comments.length === 1 && rxZone.test(comments[0])) {
		const match = rxDateTime1NoZone.exec(input);
		if (match) {
			return parse2(
				match[1],
				match[2],
				match[3],
				match[4],
				match[5],
				match[6],
				match[7],
				comments[0]
			);
		}
	}

	{
		const match = rxDateTime2.exec(input);
		if (match) {
			return parse2(
				match[2],
				match[1],
				match[3],
				match[4],
				match[5],
				match[6],
				match[7],
				match[8]
			);
		}
	}

	{
		const match = rxDateTime3.exec(input);
		if (match) {
			return parse2(
				match[2],
				match[1],
				match[8],
				match[3],
				match[4],
				match[5],
				match[6],
				match[7]
			);
		}
	}
}

export function getUnixMs(str: string): ParseResultTimestamp | undefined {
	const p = parse(str);
	if (p) {
		const { nums, ...rest } = p;
		const ms1 = Date.UTC(nums[0], nums[1], nums[2], nums[3], nums[4], nums[5], nums[6]);
		const ms2 = ms1 - p.tzOffsetMins * 60 * 1000;

		if (ms2 >= -2208988800000 && ms2 <= 4102444800000) {
			return {
				ms: ms2,
				...rest,
			};
		}
	}
}
